import { useCallback } from 'react';

/**
 * US timezones offered in the meter inspector timezone switcher.
 * Ordered roughly west-to-east within the continental US, followed by territories.
 */
export const US_TIMEZONES = [
  { value: 'America/New_York', label: 'Eastern' },
  { value: 'America/Chicago', label: 'Central' },
  { value: 'America/Denver', label: 'Mountain' },
  { value: 'America/Los_Angeles', label: 'Pacific' },
  { value: 'America/Anchorage', label: 'Alaska' },
  { value: 'Pacific/Honolulu', label: 'Hawaii' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
  { value: 'America/Adak', label: 'Hawaii-Aleutian (DST)' },
] as const;

/** Browser-resolved IANA timezone for the current session. */
export const USER_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * Short human-readable label for an IANA timezone. Falls back to the final
 * path segment (with underscores → spaces) if the zone isn't in US_TIMEZONES.
 */
export function shortTzName(iana: string): string {
  const match = US_TIMEZONES.find((tz) => tz.value === iana);
  return match?.label ?? iana.split('/').pop()!.replace(/_/g, ' ');
}

/**
 * Compute the ms difference between target timezone local time and browser local time
 * at a given moment. Adding this to a UTC timestamp and formatting as browser-local
 * gives the target timezone's local time display.
 *
 * Callers should pass the specific timestamp being shifted (not a range midpoint)
 * so that DST transitions are handled correctly for each individual timestamp.
 */
export function computeTzShiftMs(targetTz: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: targetTz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (type: string) => parseInt(parts.find((p) => p.type === type)?.value ?? '0');
  let hour = get('hour');
  if (hour === 24) hour = 0;
  const targetAsUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    hour,
    get('minute'),
    get('second'),
  );
  const browserAsUtc = Date.UTC(
    at.getFullYear(),
    at.getMonth(),
    at.getDate(),
    at.getHours(),
    at.getMinutes(),
    at.getSeconds(),
  );
  return targetAsUtc - browserAsUtc;
}

/**
 * Shift a millisecond timestamp from its original (browser-interpreted) time
 * into the target timezone's local clock time. Pass `null` for `timezoneOverride`
 * to return the timestamp unchanged (browser time). Safe to call in a loop —
 * the DST-sensitive shift is computed per-timestamp.
 */
export function shiftTimestamp(ts: number, timezoneOverride: string | null): number {
  if (timezoneOverride == null) return ts;
  const d = new Date(ts);
  return ts + computeTzShiftMs(timezoneOverride, d);
}

/**
 * Hook producing the display timezone label, an `isOverrideActive` flag, and a
 * `formatDateTime` function that renders a Date in the selected timezone
 * (or browser-local when the override is null).
 */
export function useTimezoneFormatter(timezoneOverride: string | null) {
  const isOverrideActive = timezoneOverride != null;
  const displayTimezone = isOverrideActive
    ? shortTzName(timezoneOverride)
    : shortTzName(USER_TIMEZONE);

  const formatDateTime = useCallback(
    (date: Date) => {
      if (!isOverrideActive) return date.toLocaleString();
      return date.toLocaleString(undefined, { timeZone: timezoneOverride });
    },
    [isOverrideActive, timezoneOverride],
  );

  return { displayTimezone, isOverrideActive, formatDateTime };
}
